/**
 * Formats a date to French locale with weekday, day, and month
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
	const options = { weekday: 'long', day: 'numeric', month: 'long' };
	const formattedDate = date.toLocaleDateString('fr-FR', options);
	return formattedDate.replace(/\b0+/g, '');
};

/**
 * Safe formatter for promo start/end dates from API (string, Firestore Timestamp object, or Date).
 * Always returns a string so it is safe to render in React.
 * @param {string|Date|{_seconds: number, _nanoseconds?: number}|null|undefined} value
 * @returns {string}
 */
export const formatPromoDate = (value) => {
	if (value == null) return 'N/A';
	if (typeof value === 'string') return value.includes('T') ? value.split('T')[0] : value;
	if (typeof value === 'object' && typeof value._seconds === 'number') {
		return new Date(value._seconds * 1000).toISOString().split('T')[0];
	}
	if (value.toDate && typeof value.toDate === 'function') {
		return value.toDate().toISOString().split('T')[0];
	}
	if (value instanceof Date) return value.toISOString().split('T')[0];
	if (typeof value === 'object') return 'N/A';
	return String(value);
};

/**
 * Gets the next occurrence of a specific day of the week
 * @param {number} dayIndex - Day index (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 * @returns {string} Formatted date string of the next occurrence
 */
export const getNextDay = (dayIndex) => {
	const today = new Date();
	const currentDay = today.getDay();
	const daysUntilNext = (dayIndex + 7 - currentDay) % 7 || 7;
	const nextDay = new Date(today);
	nextDay.setDate(today.getDate() + daysUntilNext);
	return formatDate(nextDay);
};

/**
 * Gets the next consecutive Thursday and Friday for Shabbat mode
 * @returns {{thursday: string, friday: string}} Object with formatted Thursday and Friday dates
 */
export const getNextShabbatDays = () => {
	const today = new Date();
	const currentDay = today.getDay();
	
	let daysUntilThursday;
	
	if (currentDay < 5) {
		daysUntilThursday = (4 - currentDay + 7) % 7;
	} else {
		daysUntilThursday = 4 + (7 - currentDay);
	}
	
	const nextThursday = new Date(today);
	nextThursday.setDate(today.getDate() + daysUntilThursday);
	
	const nextFriday = new Date(nextThursday);
	nextFriday.setDate(nextThursday.getDate() + 1);
	
	return {
		thursday: formatDate(nextThursday),
		friday: formatDate(nextFriday)
	};
};

/**
 * Gets the next recurring dates based on selected days
 * @param {Array<number|string>} recurringDays - Array of day indices
 * @returns {Array<string>} Sorted array of formatted date strings
 */
export const getNextRecurringDates = (recurringDays) => {
	if (!recurringDays || recurringDays.length === 0) return [];
	
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const datesToSort = [];
	
	recurringDays.forEach(dayId => {
		const numericDayId = typeof dayId === 'number' ? dayId : parseInt(dayId, 10);
		
		if (isNaN(numericDayId)) {
			return;
		}
		
		let daysUntilNext;
		const currentDay = today.getDay();
		
		if (numericDayId > currentDay) {
			daysUntilNext = numericDayId - currentDay;
		} else if (numericDayId === currentDay) {
			daysUntilNext = 7;
		} else {
			daysUntilNext = 7 - (currentDay - numericDayId);
		}
		
		const nextDate = new Date(today);
		nextDate.setDate(today.getDate() + daysUntilNext);
		
		if (nextDate > today) {
			const formattedDate = formatDate(nextDate);
			datesToSort.push({
				date: nextDate,
				formatted: formattedDate
			});
		}
	});
	
	datesToSort.sort((a, b) => a.date - b.date);
	return datesToSort.map(item => item.formatted);
};

/**
 * Gets the first Passover day for 2025
 * @returns {string} Formatted date string
 */
export const getNextFirstPassoverDay = () => {
	const targetDate = new Date('2025-04-12');
	return formatDate(targetDate);
};

/**
 * Gets the second Passover day for 2025
 * @returns {string} Formatted date string
 */
export const getNextSecondPassoverDay = () => {
	const targetDate = new Date('2025-04-18');
	return formatDate(targetDate);
};

